import { DataContext } from '@/types';
import { debounce } from 'lodash';
import { EventBus } from '../../common/EventBus';
import { queryKeys } from '../../hooks/queryKeys';
import { queryClient } from '../../queryClient';
import { Data } from '../Data';
import { Notification } from './Notification';
import { NotificationsConnection } from './NotificationsConnection';
import { NotificationsUnread } from './NotificationsUnread';

// Coalesce the per-skill `skillMatchesComputed` signals into a single scoped, active-only refetch of the
// match-derived career views (benchmark gaps, learning pathway, …). Scoped to the `careers` root and
// refetchType 'active' so only mounted queries refetch; debounced so a bulk-add that fans out one event
// per skill over the compute window collapses into a single refetch instead of one per skill.
const invalidateBenchmarkMatches = debounce(async () => {
  await queryClient.invalidateQueries({
    queryKey: queryKeys.careers.key,
    refetchType: 'active',
  });
  document.dispatchEvent(new CustomEvent(EventBus.event.skillMatchesComputed));
}, 500);

export class NotificationsData extends Data {
  static instance: any = {};
  static connection = null;

  mode: any;
  unread: any;
  items: any[];

  constructor(options: any) {
    super({
      api: options.mode.notificationsApi,
      callback: (data: any) => {
        options.resolve(this, false);
      },
      ...options,
    });

    this.mode = options.mode;
    this.unread = options.unread;

    this.items = [];
  }

  onDataReceived(data: any) {
    data.notifications.forEach((notif: any) => {
      const notification = new Notification(notif, this.mode);
      if (notification.isValid()) this.items.push(notification);
    });

    this.sort();
  }

  handleNotification(notif: any) {
    const existingNotif = this.getNotification(notif.showOnceId);
    notif.isNew = !existingNotif;
    if (existingNotif) {
      if (notif.isRead() && !existingNotif.isRead()) this.setAsRead(existingNotif, true);
    } else {
      if (notif.isValid()) {
        this.items.push(notif);
        this.sort();
        //this.unread.update(1);    //let server tell us
      }
    }

    this.onNotificationReceived(notif, this.unread.count);
  }

  onNotificationReceived(notif: any, count: any) { }

  sort() {
    this.items.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  setAsRead(notif: any, fromServer: any) {
    notif.setAsRead();
    //if(fromServer) this.unread.update(-1);    //let server tell us
  }

  getNotification(showOnceId: any) {
    return this.items.find((n: any) => n.showOnceId === showOnceId);
  }

  static startNotifications(context: DataContext) {
    return new Promise((resolve, reject) => {
      NotificationsConnection.getNotificationsConnection(context)
        .then((connection: any) => {
          connection.onSkillMatchesComputed = () => {
            invalidateBenchmarkMatches();

          };

          connection.onNotificationReceived = (notif: any, numUnread: any) => {
            NotificationsData.getNotificationsData(context, notif.dashboardMode)
              .then((n: any) => {
                n.unread.set(numUnread);

                if (!n.hasInit) {
                  resolve(n);
                  n.hasInit = true;
                }

                Promise.allSettled([this]).then((e) => {
                  n.handleNotification(notif);
                });
              })
              .catch((e) => reject(e))
              .finally();
          };
        })
        .catch((e) => reject(e));
    });
  }

  static getNotificationsData(context: DataContext, mode: any): Promise<NotificationsData> {
    return new Promise((resolve, reject) => {
      if (NotificationsData.instance[mode.name]) resolve(NotificationsData.instance[mode.name]);
      else
        NotificationsUnread.getNotificationsUnread(context).then((unread) => {
          NotificationsData.instance[mode.name] = new NotificationsData({
            resolve,
            reject,
            context,
            mode,
            unread,
          });
        });
    });
  }
}
